import { Box, Flex } from '@chakra-ui/react';
import { ButtonText, RadioCard } from '@morressier/alembic';
import React, { useState } from 'react';
import { QueryKey } from '@tanstack/react-query';
import { SubmissionAssignment } from './Assignment';
import { EditorialRoleInfo } from './EditorialRoleInfo';
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSubmissionData } from '../../contexts/SubmissionDataProvider';
import { IGetSubmissionEditorialAssigneesResponse } from '@publishing-engine/submission-service/src/endpoints/api.response-types';
import { DecisionMenuActions } from 'src/components/Submission/decisionModals/decisionModalHelper';
import { getModalForDecision } from 'src/components/Submission/decisionModals/decisionModalHelper';
import { IDecisionModal } from 'src/components/Submission/decisionModals/decisionModalHelper';
import { AdditionalInformation } from 'src/components/Submission/AdditionalInformation';
import { endStates, IEndState } from '@publishing-engine/domain/models';
import { EndState } from 'src/components/EndState';
import { SubmissionDetailTab } from '.';
import { EditorialWorkPhaseActions } from './WorkPhaseActions';

import { useSubmissionContextData } from 'src/contexts/SubmissionContextProvider';
import { useCOCContextData } from 'src/contexts/COCDataProvider';
import { DecisionMakerWorkPhase } from './DecisionMakerWorkPhase';
import RecommendationForm from './RecommendationForm';
import { getActionLabelAndDescription } from 'src/utils/displayHelpers';
import { COCSubmissionAssignment } from './COCSubmissionAssignment';
import { SubmissionTasks } from './SubmissionTasks';

interface Props {
  onClose?: () => void;
  queryKey: QueryKey;
  editorialAssignees: IGetSubmissionEditorialAssigneesResponse['editorialAssignees'];
  setActiveTab?: (tab: any) => void;
  activeTab: SubmissionDetailTab;
}

export const Sidebar: React.FC<Props> = ({
  editorialAssignees,
  setActiveTab
}) => {
  const { workflowResponse, organizationId,isDecisionMaker } = useSubmissionData();
  const { submissionContext } = useSubmissionContextData();
  const {
    recommedationSummary,
    isCurrentOwner,
  } = useCOCContextData();
  const { submissionId, submissionContextId, workflowData, articleWorkflow } =
    workflowResponse;
  const transitionGraph = articleWorkflow.transitionGraph;

  const { decisionMenuConfig } = submissionContext?.config ?? {};

  const [showingDecisionsForPhase, setShowingDecisionsForPhase] = useState<
    string | null
  >(null);
  const [showingDecisionModal, setShowingDecisionModal] = useState<
    string | null
  >(null);

  const currentPhase = {
    name: workflowData.currentState,
    completedWith: undefined
  };

  const workflowStateConfig =
    transitionGraph.submission.states[currentPhase?.name!]!;

  const decisionActionNames = workflowStateConfig.actions
    .filter(
      actionReference =>
        actionReference.displayProperties.location === 'decisionMenu' &&
        decisionMenuConfig &&
        actionReference.name in decisionMenuConfig
    )
    .map(actionReference => actionReference.name) as DecisionMenuActions[];

  let Modal: IDecisionModal | undefined = undefined;
  if (showingDecisionModal) {
    Modal = getModalForDecision(showingDecisionModal as DecisionMenuActions);
  }

  const isEndState = endStates.includes(workflowData.currentState as IEndState);
  const isCOCEnabled = (submissionContext?.config.cocConfig?.length ?? 0) !== 0;
  const endStateActions = workflowStateConfig.actions
    .filter(
      actionReference =>
        actionReference.displayProperties.location === 'contextMenu' &&
        // Archived submissions (the Archive tab) are final, so Revert Decision
        // is withheld even when `canRevertDecision` offers it.
        actionReference.name !== 'RevertDecision'
    )
    .map(actionReference => {
      const actionData = getActionLabelAndDescription(actionReference.name);

      return {
        name: actionReference.name,
        action: () => setShowingDecisionModal(actionReference.name),
        label: actionData?.label || actionReference.name,
        description: actionData?.description ?? '',
        headingColor: actionData?.headingColor
      };
    });

  return (
    <>
      {/* ---- Decision Modal ---- */}
      {showingDecisionModal && Modal ? (
        <Modal
          onClose={() => {
            setShowingDecisionModal(null);
            setShowingDecisionsForPhase(null);
          }}
          submissionId={submissionId}
          organizationId={organizationId}
          submissionContextId={submissionContextId}
          setActiveTab={setActiveTab}
        />
      ) : null}

      {/* ----- Sidebar ----- */}
      <Flex width="100%" flexDirection="column">
        <Flex flexDirection="column" gap={4} padding={4}>
          <>
            <COCSubmissionAssignment submissionId={submissionId} />

            {!isCOCEnabled && (
              <SubmissionAssignment
                assigneeId={
                  editorialAssignees?.find(
                    assignee => assignee.role === 'owner'
                  )?.userId
                }
                submissionId={submissionId}
              />
            )}

            <SubmissionTasks setActiveTab={setActiveTab} />

            {showingDecisionsForPhase ? (
              <>
                <Flex>
                  <ButtonText
                    onClick={() => setShowingDecisionsForPhase(null)}
                    iconLeft={<FontAwesomeIcon icon={faArrowLeft} />}
                  >
                    Back
                  </ButtonText>
                </Flex>
                <Flex flexDirection="column" gap={4}>
                  {decisionActionNames.map(action => {
                    return (
                      <RadioCard
                        register={null}
                        checked={false}
                        ariaLabel={action}
                        onClick={() => {
                          setShowingDecisionModal(action);
                        }}
                        title={decisionMenuConfig?.[action]?.label || action}
                        body={decisionMenuConfig?.[action]?.description}
                        width="100%"
                      />
                    );
                  })}
                </Flex>
              </>
            ) : (
              <Box>
                <EditorialRoleInfo />
                <Flex direction={'column'} gap={4}>
                  <Flex flexDirection={'column'}>
                    {isEndState ? (
                      <EndState
                        role="editorial"
                        currentWorkflowState={
                          workflowData.currentState as IEndState
                        }
                        dateEnteredCurrentPhase={
                          workflowData.dateEnteredCurrentPhase
                        }
                        actions={endStateActions}
                      />
                    ) : (
                      <DecisionMakerWorkPhase
                        showingDecisionsForPhase={showingDecisionsForPhase}
                        phase={currentPhase}
                        decisionActionNames={decisionActionNames}
                        setShowingDecisionModal={setShowingDecisionModal}
                        latestRole={
                          recommedationSummary?.lastRecommendationOrDecisionRole ??
                          ''
                        }
                        latestRecommendation={
                          recommedationSummary?.lastRecommendationOrDecision ??
                          ''
                        }
                        isCurrentOwner={isCurrentOwner}
                        isCOCEnabled={isCOCEnabled}
                        isCOCDecisionMaker={isDecisionMaker}
                        workflowStateConfig={workflowStateConfig}
                        setShowingDecisionsForPhase={
                          setShowingDecisionsForPhase
                        }
                      />
                    )}

                    {!isCurrentOwner && !isCOCEnabled && (
                      <EditorialWorkPhaseActions
                        phase={currentPhase}
                        workflowStateConfig={workflowStateConfig}
                        showingDecisionsForPhase={showingDecisionsForPhase}
                        setShowingDecisionsForPhase={
                          setShowingDecisionsForPhase
                        }
                        setShowingDecisionModal={setShowingDecisionModal}
                      />
                    )}
                  </Flex>
                  {isCOCEnabled && !isCurrentOwner && <RecommendationForm />}
                  <AdditionalInformation
                    role="editorial"
                    workflowResponse={workflowResponse}
                    setActiveTab={setActiveTab}
                  />
                </Flex>
              </Box>
            )}
          </>
        </Flex>
      </Flex>
    </>
  );
};
